import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Icons } from '../components/Icons';
import { useAuth } from '../hooks/useAuth';
import { getUserDisplayName } from '../utils/userMapping';
import * as XLSX from 'xlsx';
import MonthlyStatsModal from '../components/MonthlyStatsModal';

// 카드수수료율 기본값 (2025.8.14 기준 중소 가맹점 적용)
const CARD_FEE_RATES = {
  'KB국민': 1.45,
  '신한': 1.45,
  '삼성': 1.45,
  '현대': 1.45,
  '롯데': 1.45,
  '우리': 1.45,
  'NH농협': 1.45,
  '하나': 1.45,
  'BC': 1.45,
  '토스': 3.3,
  '네이버페이': 3.3,
  '카카오페이': 2.5,
  '페이업': 3.4,
  '기타': 1.45
};

// 판매 장소 옵션
const SALE_LOCATIONS = ['온라인', 'APF마켓', '자곡동', '행사', '기타'];

// 택배사 옵션
const COURIER_COMPANIES = ['CJ 대한통운', '한진택배', '롯데택배', '우체국택배', '로젠택배', '직접배송', '기타'];

function SalesHistory() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [showMonthlyStatsModal, setShowMonthlyStatsModal] = useState(false);
  const [selectedSaleDetail, setSelectedSaleDetail] = useState(null); // 상세정보 모달
  const [navigationModal, setNavigationModal] = useState(null); // 제품명 클릭 시 이동 선택 모달
  const [searchQuery, setSearchQuery] = useState(''); // 매출 검색
  const [sortOrder, setSortOrder] = useState('desc'); // 날짜 정렬 순서
  const [showFilters, setShowFilters] = useState(true); // 필터 접기/펼치기 (데스크탑 기본: 펼침)
  const [selectedSkus, setSelectedSkus] = useState([]); // SKU 개별 선택 (신규 등록용)
  const [editingSkus, setEditingSkus] = useState([]); // SKU 수정용 (매출 수정 시)
  const [editingProduct, setEditingProduct] = useState(null); // 수정 중인 매출의 제품 정보
  const [invoiceDateModal, setInvoiceDateModal] = useState({ show: false, saleId: null, invoiceType: '', date: '' }); // 발행일 선택 모달
  const navigate = useNavigate();

  // 설정 상태
  const [config, setConfig] = useState({
    saleLocations: SALE_LOCATIONS,
    paymentMethods: ['입금', '카드', '현금', '토스', '네이버', '페이업'],
    cardFeeRates: CARD_FEE_RATES
  });

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    dateType: 'saleDate', // 조회 기준: saleDate(판매일) 또는 paymentDate(입금일)
    saleLocation: '',
    paymentMethod: '',
    ownershipType: '',
    tradeType: '',
    paymentStatus: '', // 완납, 미입금, 부분입금
    invoiceIssued: '', // 계산서 발행 여부
    shippingStatus: '',
  });

  // 새 매출 기록 폼 데이터 - 엑셀 양식에 맞춤
  const initialFormData = {
    saleDate: new Date().toISOString().split('T')[0],
    // 고객 정보
    buyerName: '',
    buyerContact: '',
    buyerAddress: '',
    // 제품 정보 (선택된 제품에서 자동)
    productId: '',
    codeNo: '',
    categoryLarge: '',
    categoryMedium: '',
    itemName: '',
    designer: '',
    manufacturer: '',
    // C/M (색상/소재)
    colorMaterial: '', // C/M (Color/Material)
    // 직판/위탁 구분
    ownershipType: '직판', // 직판/위탁
    ownerName: 'OOM', // 위탁자 이름 또는 OOM
    // 결제 정보
    paymentMethod: '입금', // 입금, 카드, 현금, 토스, 네이버, 페이업
    cardCompany: '',
    cardFeeRate: 0, // 입금 기본 0%, 카드 선택 시 1.45%
    // 입금 상태
    paymentStatus: '완납', // 완납, 미입금, 부분입금
    paidAmount: 0, // 입금된 금액 (부분입금 시)
    paymentDate: '', // 입금일 (실제 입금 완료일)
    // 수량 및 금액
    quantity: 1,
    stockQuantity: 1, // 선택된 제품의 재고 수량
    price: 0, // 단가
    deliveryFee: 0, // 배송비
    installationFee: 0, // 설치비
    // 문서
    hasContract: false, // 구매 계약서 체크
    hasWarranty: false, // 워런티카드 작성 체크
    warranty: '', // 워런티 정보
    invoiceNeeded: false, // 계산서/영수증 발행 필요 여부
    invoiceType: '미발행', // 미발행/세금계산서/현금영수증
    invoiceIssuedDate: '', // 발행일
    // 배송 정보
    shippingMethod: '일정 조율 필요', // 발송 수단: 일정 조율 필요, 자사 물류, 외주(퀵), 택배
    deliveryInfo: '',
    courierCompany: '', // 택배사
    trackingNumber: '', // 운송장 번호
    shippingStatus: '준비중',
    scheduledDeliveryDate: '', // 자사 물류 예정일
    scheduledDeliveryTime: '', // 자사 물류 예정 시간
    shippingCompletedDate: '', // 배송 완료일
    // 기타
    memo: '',
    seller: getUserDisplayName(user?.email) || '', // 판매자 (이름)
    saleLocation: '자곡동', // 판매 장소
    tradeType: '도소매', // 도소매/중개업 구분
    // 원가 정보
    purchasePrice: 0 // 원가(매입)
  };

  const [formData, setFormData] = useState(initialFormData);

  // user 로드 후 seller 업데이트
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        seller: getUserDisplayName(user.email)
      }));
    }
  }, [user]);

  const loadData = async () => {
    try {
      // 매출 데이터 로드
      const salesRef = collection(db, 'sales');
      const q = query(salesRef, orderBy('saleDate', 'desc'));
      const salesSnapshot = await getDocs(q);
      const salesData = salesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        saleDate: doc.data().saleDate?.toDate?.() || new Date(doc.data().saleDate)
      }));
      setSales(salesData);

      // 재고 데이터 로드 (전체 - 판매완료 포함, 매출 기록과 매칭용)
      const inventoryRef = collection(db, 'inventory');
      const invSnapshot = await getDocs(inventoryRef);
      const invData = invSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInventory(invData);

    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesConfig = async () => {
    try {
      const configRef = doc(db, 'settings', 'sales');
      const snapshot = await getDoc(configRef);

      if (snapshot.exists()) {
        const data = snapshot.data();
        setConfig(prev => ({
          ...prev,
          saleLocations: data.saleLocations || prev.saleLocations,
          paymentMethods: data.paymentMethods || prev.paymentMethods,
          cardFeeRates: data.cardFeeRates || prev.cardFeeRates
        }));
      }
    } catch (error) {
      console.error('설정 로드 오류:', error);
    }
  };

  useEffect(() => {
    loadData();
    fetchSalesConfig();
  }, [user]);

  // URL에 productId가 있으면 해당 제품을 자동 선택하고 매출 등록 모달 열기
  useEffect(() => {
    const productId = searchParams.get('productId');
    if (productId && inventory.length > 0 && !loading) {
      const product = inventory.find(p => p.id === productId);
      if (product) {
        // 제품 자동 선택
        handleSelectProduct(product);
        // 모달 열기
        setShowAddModal(true);
        // URL에서 productId 제거 (뒤로가기 시 다시 모달 열리지 않도록)
        setSearchParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, loading, searchParams]);

  const handleStatusChange = async (saleId, field, value) => {
    const isBoolean = typeof value === 'boolean';
    const parsedValue = isBoolean ? value : (value === 'true' ? true : (value === 'false' ? false : value));

    // 현재 값 가져오기 (변경 이력용)
    const currentSale = sales.find(s => s.id === saleId);
    const previousValue = currentSale?.[field];

    try {
      const saleRef = doc(db, 'sales', saleId);

      // 변경 이력 기록 객체
      const historyEntry = {
        field,
        previousValue: previousValue ?? null,
        newValue: parsedValue,
        changedAt: new Date().toISOString(),
        changedBy: user?.email || 'unknown',
        changedByName: getUserDisplayName(user?.email) || 'unknown'
      };

      await updateDoc(saleRef, {
        [field]: parsedValue,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || 'unknown',
        changeHistory: arrayUnion(historyEntry)
      });

      setSales(prevSales =>
        prevSales.map(sale =>
          sale.id === saleId ? {
            ...sale,
            [field]: parsedValue,
            changeHistory: [...(sale.changeHistory || []), historyEntry]
          } : sale
        )
      );
    } catch (error) {
      console.error('상태 업데이트 오류:', error);
      alert('상태 업데이트 중 오류가 발생했습니다.');
      loadData();
    }
  };

  // 제품 검색 결과 (판매가능 상태만, 수량 1개 이상)
  const filteredProducts = useMemo(() => {
    // 판매 가능한 제품만 필터링
    const availableInventory = inventory.filter(item =>
      item.status !== '판매완료' && (item.quantity || 0) > 0
    );

    if (!productSearchQuery) return availableInventory.slice(0, 50); // 50개로 증가
    const q = productSearchQuery.toLowerCase();
    return availableInventory.filter(item =>
      item.productName?.toLowerCase().includes(q) ||
      item.sku?.toLowerCase().includes(q) ||
      item.barcodeId?.toLowerCase().includes(q) ||
      item.designer?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [inventory, productSearchQuery]);

  // 제품 선택 시
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSelectedSkus([]); // SKU 선택 초기화
    setFormData(prev => ({
      ...prev,
      productId: product.id,
      codeNo: product.sku || '',
      categoryLarge: product.categoryLarge || '',
      categoryMedium: product.categoryMedium || '',
      itemName: product.productName || '',
      designer: product.designer || '',
      manufacturer: product.manufacturer || '',
      ownershipType: product.ownershipType === '위탁' ? '위탁' : '직판',
      ownerName: product.ownershipType === '위탁' ? (product.consignorName || '') : 'OOM',
      price: Math.round((product.salePrice || 0) * 1.1), // 부가세 포함 금액으로 변환
      purchasePrice: product.purchasePrice || 0,
      quantity: 1,
      stockQuantity: Number(product.quantity) || 1 // 재고 수량 저장
    }));
    setShowProductSearch(false);
    setProductSearchQuery('');
  };

  // SKU 선택 토글
  const handleSkuToggle = (sku) => {
    setSelectedSkus(prev => {
      if (prev.includes(sku)) {
        return prev.filter(s => s !== sku);
      } else {
        return [...prev, sku];
      }
    });
  };

  // SKU 전체 선택/해제
  const handleSelectAllSkus = () => {
    if (!selectedProduct?.skuLocations) return;
    if (selectedSkus.length === selectedProduct.skuLocations.length) {
      setSelectedSkus([]);
    } else {
      setSelectedSkus(selectedProduct.skuLocations.map(s => s.sku));
    }
  };

  // 수정 모드용 SKU 제거
  const handleEditingSkuRemove = (fullSku) => {
    setEditingSkus(prev => prev.filter(s => s !== fullSku));
  };

  // 수정 모드용 SKU 추가 (재고에서 선택)
  const handleEditingSkuAdd = (skuItem) => {
    if (!editingProduct) return;
    const fullSku = `${editingProduct.barcodeId}-${skuItem.sku}`;
    if (!editingSkus.includes(fullSku)) {
      setEditingSkus(prev => [...prev, fullSku]);
    }
  };

  // 수정 모드에서 선택 가능한 재고 SKU 목록
  const getAvailableSkusForEdit = () => {
    if (!editingProduct?.skuLocations) return [];
    // 현재 재고에 있는 SKU 중에서 아직 선택되지 않은 것들
    return editingProduct.skuLocations.filter(skuItem => {
      const fullSku = `${editingProduct.barcodeId}-${skuItem.sku}`;
      return !editingSkus.includes(fullSku);
    });
  };

  // 계산 함수 - 엑셀 공식에 맞춤
  const calculateTotals = (data) => {
    const qty = parseInt(data.quantity) || 1;
    const price = parseInt(data.price) || 0;
    const deliveryFee = parseInt(data.deliveryFee) || 0;
    const installationFee = parseInt(data.installationFee) || 0;
    const cardFeeRate = parseFloat(data.cardFeeRate) || 0;
    const purchasePrice = parseInt(data.purchasePrice) || 0;

    // total (price + 배송비 + 설치비)
    const total = (price * qty) + deliveryFee + installationFee;

    // PRICE (VAT 포함 = total)
    const priceInclVat = total;

    // 부가세 = PRICE - (PRICE / 1.1)
    const vat = Math.round(priceInclVat - (priceInclVat / 1.1));

    // 매출 기준 금액 = PRICE / 1.1 (VAT 제외)
    const salesBaseAmount = Math.round(priceInclVat / 1.1);

    // 원가(매입)
    const costPrice = purchasePrice * qty;

    // 매출액 Total = 매출 기준 금액 (도소매) 또는 수수료 (중개업)
    let salesTotal = 0;
    if (data.tradeType === '도소매') {
      salesTotal = salesBaseAmount;
    } else if (data.tradeType === '중개업') {
      // 중개업: 수수료만 매출로 인정 (예: 10% 수수료)
      salesTotal = Math.round(salesBaseAmount * 0.1);
    }

    // 거래액 Total = PRICE
    const transactionTotal = priceInclVat;

    // 카드수수료 = PRICE * 수수료율
    const cardFee = cardFeeRate > 0 ? Math.round(priceInclVat * cardFeeRate / 100) : 0;

    // 순수익 = 매출 기준 금액 - 원가 - 카드수수료
    let netProfit = 0;
    if (data.ownershipType === '위탁') {
      // 위탁: 수수료 수익만
      const commissionRate = 0.2; // 20% 수수료 가정
      netProfit = Math.round(salesBaseAmount * commissionRate) - cardFee;
    } else {
      // 매입: 판매가 - 원가 - 카드수수료
      netProfit = salesBaseAmount - costPrice - cardFee;
    }

    // 수익률 = 순수익 / 거래액
    const profitRate = transactionTotal > 0 ? (netProfit / transactionTotal) : 0;

    return {
      total,
      priceInclVat,
      vat,
      salesBaseAmount,
      costPrice,
      salesTotal,
      transactionTotal,
      cardFee,
      netProfit,
      profitRate
    };
  };

  // 폼 변경 핸들러
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // 카드사 선택 시 수수료율 자동 설정
    if (name === 'cardCompany') {
      const rate = config.cardFeeRates[value] || 1.45;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        cardFeeRate: rate
      }));
      return;
    }

    // 결제 수단 변경 시 수수료율 자동 설정
    if (name === 'paymentMethod') {
      let rate = 0;
      if (value === '카드') {
        rate = config.cardFeeRates[formData.cardCompany] || 1.45;
      } else if (value === '토스' || value === '네이버' || value === '네이버페이') {
        rate = 3.3;
      } else if (value === '페이업') {
        rate = 3.4; // 페이업 기본 수수료율 (가정)
      }

      // 설정에 있는 경우 우선 적용
      if (config.cardFeeRates[value]) {
        rate = config.cardFeeRates[value];
      }

      setFormData(prev => ({
        ...prev,
        [name]: value,
        cardFeeRate: rate
      }));
      return;
    }

    // 입금 상태 변경 시
    if (name === 'paymentStatus') {
      setFormData(prev => ({
        ...prev,
        [name]: newValue,
        paidAmount: newValue === '완납' ? 0 : (newValue === '미입금' ? 0 : prev.paidAmount)
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  // 매출 등록/수정
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.itemName) {
      alert('제품을 선택하거나 제품명을 입력해주세요.');
      return;
    }

    // SKU가 있는 제품일 경우 SKU 선택 검증 (신규 등록 시에만)
    const hasSkuLocations = selectedProduct?.skuLocations && selectedProduct.skuLocations.length > 0;
    if (!editingSale && hasSkuLocations && selectedSkus.length === 0) {
      alert('판매할 SKU를 선택해주세요.');
      return;
    }

    // SKU 선택 시 수량 자동 설정 (수정 시 editingSkus 사용)
    let saleQuantity;
    if (editingSale) {
      // 수정 모드: editingSkus 사용
      saleQuantity = editingSkus.length > 0 ? editingSkus.length : (parseInt(formData.quantity) || 1);
    } else {
      // 신규 등록: selectedSkus 사용
      saleQuantity = hasSkuLocations && selectedSkus.length > 0
        ? selectedSkus.length
        : (parseInt(formData.quantity) || 1);
    }

    const totals = calculateTotals({ ...formData, quantity: saleQuantity });

    try {
      const saleData = {
        // 기본 정보
        saleDate: new Date(formData.saleDate),
        // 고객 정보
        buyerName: formData.buyerName,
        buyerContact: formData.buyerContact,
        buyerAddress: formData.buyerAddress,
        // 제품 정보
        productId: formData.productId || null,
        codeNo: formData.codeNo,
        categoryLarge: formData.categoryLarge,
        categoryMedium: formData.categoryMedium,
        itemName: formData.itemName,
        designer: formData.designer,
        manufacturer: formData.manufacturer,
        // 소유 정보
        ownershipType: formData.ownershipType,
        ownerName: formData.ownerName,
        // 결제 정보
        paymentMethod: formData.paymentMethod,
        cardCompany: formData.cardCompany,
        cardFeeRate: formData.cardFeeRate,
        paymentStatus: formData.paymentStatus,
        paymentDate: formData.paymentDate || '',
        // 수량 및 금액
        quantity: saleQuantity,
        price: parseInt(formData.price),
        // 판매된 SKU 정보 (수정 시 editingSkus 사용)
        soldSkus: editingSale
          ? editingSkus  // 수정 모드: editingSkus 사용 (이미 full SKU 형식)
          : (hasSkuLocations && selectedSkus.length > 0
              ? selectedSkus.map(sku => `${selectedProduct.barcodeId}-${sku}`)
              : []),
        deliveryFee: parseInt(formData.deliveryFee) || 0,
        installationFee: parseInt(formData.installationFee) || 0,
        total: totals.total,
        // 문서
        hasContract: formData.hasContract || false,
        warranty: formData.warranty || '',
        invoiceNeeded: formData.invoiceNeeded || false,
        invoiceType: formData.invoiceType || '미발행',
        invoiceIssuedDate: formData.invoiceIssuedDate || '',
        // 배송 정보
        shippingMethod: formData.shippingMethod || '일정 조율 필요',
        deliveryInfo: formData.deliveryInfo || '',
        courierCompany: formData.courierCompany || '',
        trackingNumber: formData.trackingNumber || '',
        shippingStatus: formData.shippingStatus || '준비중',
        scheduledDeliveryDate: formData.scheduledDeliveryDate || '',
        scheduledDeliveryTime: formData.scheduledDeliveryTime || '',
        shippingCompletedDate: formData.shippingCompletedDate || '',
        // 기타
        memo: formData.memo || '',
        seller: formData.seller || '',
        saleLocation: formData.saleLocation || '자곡동',
        // 레거시 호환 (기존 데이터용)
        invoiceIssued: formData.invoiceType !== '미발행',
        receiptIssued: formData.invoiceType === '현금영수증',
        tradeType: formData.tradeType || '도소매',
        // 원가
        purchasePrice: parseInt(formData.purchasePrice) || 0,
        // 계산된 금액
        priceInclVat: totals.priceInclVat,
        vat: totals.vat,
        salesBaseAmount: totals.salesBaseAmount,
        costPrice: totals.costPrice,
        salesTotal: totals.salesTotal,
        transactionTotal: totals.transactionTotal,
        cardFee: totals.cardFee,
        netProfit: totals.netProfit,
        profitRate: totals.profitRate,
        // 메타
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || 'unknown',
        // 레거시 호환
        productName: formData.itemName,
        actualSalePrice: totals.total,
        profit: totals.netProfit
      };

      if (editingSale) {
        // 수정
        const originalSkus = editingSale.soldSkus || [];
        const newSkus = editingSkus;

        // SKU 변경 계산
        const removedSkus = originalSkus.filter(sku => !newSkus.includes(sku));
        const addedSkus = newSkus.filter(sku => !originalSkus.includes(sku));

        // 재고 동기화가 필요한 경우 (productId가 있고 SKU 변경이 있을 때)
        if (editingSale.productId && (removedSkus.length > 0 || addedSkus.length > 0)) {
          try {
            // 현재 재고 정보 가져오기 (최신 상태)
            const inventoryRef = doc(db, 'inventory', editingSale.productId);
            const inventorySnap = await getDoc(inventoryRef);

            if (inventorySnap.exists()) {
              const inventoryData = inventorySnap.data();
              let currentSkuLocations = inventoryData.skuLocations || [];
              let currentQuantity = Number(inventoryData.quantity) || 0;

              // 1. 제거된 SKU를 재고로 복원
              if (removedSkus.length > 0) {
                removedSkus.forEach(fullSku => {
                  // fullSku는 "BARCODE-01" 형식
                  const skuPart = fullSku.split('-').pop(); // "01" 추출
                  // 이미 존재하지 않으면 추가
                  const exists = currentSkuLocations.some(s => s.sku === skuPart);
                  if (!exists) {
                    currentSkuLocations.push({
                      sku: skuPart,
                      location: inventoryData.location || '자곡동 매장' // 기본 위치
                    });
                  }
                });
                currentQuantity += removedSkus.length;
              }

              // 2. 추가된 SKU를 재고에서 차감
              if (addedSkus.length > 0) {
                addedSkus.forEach(fullSku => {
                  const skuPart = fullSku.split('-').pop();
                  currentSkuLocations = currentSkuLocations.filter(s => s.sku !== skuPart);
                });
                currentQuantity = Math.max(0, currentQuantity - addedSkus.length);
              }

              // 재고 업데이트
              const updateData = {
                quantity: currentQuantity,
                skuLocations: currentSkuLocations,
                updatedAt: serverTimestamp()
              };

              // 재고가 0이면 판매완료, 아니면 판매가능
              if (currentQuantity <= 0) {
                updateData.status = '판매완료';
              } else if (inventoryData.status === '판매완료') {
                updateData.status = '판매가능';
              }

              await updateDoc(inventoryRef, updateData);

              // 이력 기록
              await addDoc(collection(db, 'inventory_history'), {
                productId: editingSale.productId,
                changeType: '매출수정',
                removedSkus: removedSkus,
                addedSkus: addedSkus,
                newQuantity: currentQuantity,
                timestamp: serverTimestamp(),
                userId: user?.email || 'unknown'
              });
            }
          } catch (error) {
            console.error('재고 동기화 오류:', error);
            // 재고 동기화 실패해도 매출 수정은 진행
          }
        }

        await updateDoc(doc(db, 'sales', editingSale.id), saleData);
        alert('매출이 수정되었습니다!' +
          (removedSkus.length > 0 ? `\n복원된 SKU: ${removedSkus.length}개` : '') +
          (addedSkus.length > 0 ? `\n차감된 SKU: ${addedSkus.length}개` : ''));
      } else {
        // 신규 등록
        saleData.createdAt = serverTimestamp();
        saleData.createdBy = user?.email || 'unknown';
        await addDoc(collection(db, 'sales'), saleData);

        // 재고 상태 업데이트 - 수량 및 SKU 고려
        if (formData.productId) {
          const inventoryRef = doc(db, 'inventory', formData.productId);
          const stockQty = parseInt(formData.stockQuantity) || 1;
          const remainingQty = stockQty - saleQuantity;

          // SKU가 있는 제품일 경우 skuLocations 업데이트
          let updatedSkuLocations = selectedProduct?.skuLocations || [];
          if (hasSkuLocations && selectedSkus.length > 0) {
            updatedSkuLocations = updatedSkuLocations.filter(s => !selectedSkus.includes(s.sku));
          }

          if (remainingQty <= 0) {
            // 재고가 모두 소진된 경우 - 판매완료 처리
            await updateDoc(inventoryRef, {
              status: '판매완료',
              quantity: 0,
              skuLocations: [],
              soldAt: serverTimestamp(),
              soldBy: user?.email || 'unknown'
            });
          } else {
            // 아직 재고가 남은 경우 - 수량과 SKU 동기화
            await updateDoc(inventoryRef, {
              quantity: remainingQty,
              skuLocations: updatedSkuLocations
            });
          }

          // 이력 기록
          await addDoc(collection(db, 'inventory_history'), {
            productId: formData.productId,
            changeType: remainingQty <= 0 ? '판매완료' : '부분판매',
            saleQuantity: saleQuantity,
            soldSkus: hasSkuLocations && selectedSkus.length > 0
              ? selectedSkus.map(sku => `${selectedProduct.barcodeId}-${sku}`)
              : [],
            remainingQuantity: Math.max(0, remainingQty),
            timestamp: serverTimestamp(),
            userId: user?.email || 'unknown'
          });
        }
        alert('매출이 등록되었습니다!');
      }

      setShowAddModal(false);
      setEditingSale(null);
      resetForm();
      loadData();

    } catch (error) {
      console.error('매출 저장 오류:', error);
      alert('매출 저장 중 오류가 발생했습니다.');
    }
  };

  // 매출 삭제
  const handleDelete = async (sale) => {
    if (!window.confirm('이 매출 기록을 삭제하시겠습니까?')) return;

    // productId가 있으면 재고 복원 여부 확인
    let restoreInventory = false;
    if (sale.productId) {
      restoreInventory = window.confirm(
        '연결된 재고를 "판매가능" 상태로 복원하시겠습니까?\n\n' +
        '• 확인: 재고 상태를 "판매가능"으로 변경\n' +
        '• 취소: 매출 기록만 삭제 (재고 상태 유지)'
      );
    }

    try {
      // 매출 기록 삭제
      await deleteDoc(doc(db, 'sales', sale.id));

      // 재고 복원 (선택한 경우)
      if (restoreInventory && sale.productId) {
        const inventoryRef = doc(db, 'inventory', sale.productId);
        await updateDoc(inventoryRef, {
          status: '판매가능',
          updatedAt: serverTimestamp()
        });
      }

      alert(restoreInventory
        ? '매출이 삭제되고 재고가 복원되었습니다.'
        : '매출이 삭제되었습니다.'
      );
      loadData();
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 매출 수정 모달 열기
  const handleEdit = async (sale) => {
    setEditingSale(sale);
    setFormData({
      saleDate: sale.saleDate?.toISOString?.()?.split('T')[0] || new Date().toISOString().split('T')[0],
      buyerName: sale.buyerName || '',
      buyerContact: sale.buyerContact || '',
      buyerAddress: sale.buyerAddress || '',
      productId: sale.productId || '',
      codeNo: sale.codeNo || sale.sku || '',
      categoryLarge: sale.categoryLarge || '',
      categoryMedium: sale.categoryMedium || '',
      itemName: sale.itemName || sale.productName || '',
      designer: sale.designer || '',
      manufacturer: sale.manufacturer || '',
      ownershipType: sale.ownershipType || '직판',
      ownerName: sale.ownerName || 'OOM',
      paymentMethod: sale.paymentMethod || '입금',
      paymentStatus: sale.paymentStatus || '완납',
      paymentDate: sale.paymentDate || '',
      cardCompany: sale.cardCompany || '',
      cardFeeRate: sale.cardFeeRate || 0,
      quantity: sale.quantity || 1,
      price: sale.price || sale.unitPrice || 0,
      deliveryFee: sale.deliveryFee || 0,
      installationFee: sale.installationFee || 0,
      hasContract: sale.hasContract || false,
      warranty: sale.warranty || '',
      deliveryInfo: sale.deliveryInfo || '',
      courierCompany: sale.courierCompany || '',
      trackingNumber: sale.trackingNumber || '',
      shippingMethod: sale.shippingMethod || '일정 조율 필요',
      scheduledDeliveryDate: sale.scheduledDeliveryDate || '',
      scheduledDeliveryTime: sale.scheduledDeliveryTime || '',
      shippingCompletedDate: sale.shippingCompletedDate || '',
      shippingStatus: sale.shippingStatus || '준비중',
      memo: sale.memo || sale.notes || '',
      seller: sale.seller || '',
      saleLocation: sale.saleLocation || sale.channel || '온라인',
      invoiceNeeded: sale.invoiceNeeded || false,
      // 레거시 데이터 변환: invoiceType이 있으면 사용, 없으면 기존 필드에서 추론
      invoiceType: sale.invoiceType || (sale.receiptIssued ? '현금영수증' : (sale.invoiceIssued ? '세금계산서' : '미발행')),
      invoiceIssuedDate: sale.invoiceIssuedDate || '',
      tradeType: sale.tradeType || '도소매',
      purchasePrice: sale.purchasePrice || 0
    });

    // 기존 판매된 SKU 초기화
    setEditingSkus(sale.soldSkus || []);

    // 제품 정보 로드 (SKU 선택을 위해)
    if (sale.productId) {
      try {
        const productDoc = await getDoc(doc(db, 'inventory', sale.productId));
        if (productDoc.exists()) {
          const productData = { id: productDoc.id, ...productDoc.data() };
          setEditingProduct(productData);
        } else {
          setEditingProduct(null);
        }
      } catch (error) {
        console.error('제품 로드 오류:', error);
        setEditingProduct(null);
      }
    } else {
      setEditingProduct(null);
    }

    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      ...initialFormData,
      seller: getUserDisplayName(user?.email) || ''
    });
    setSelectedProduct(null);
    setSelectedSkus([]);
    setEditingSale(null);
    setEditingSkus([]);
    setEditingProduct(null);
  };

  // 필터링된 매출
  const filteredSales = useMemo(() => {
    let filtered = [...sales];

    // 날짜 필터 (조회 기준에 따라)
    const getFilterDate = (sale) => {
      if (filters.dateType === 'paymentDate') {
        // 입금일 기준: paymentDate가 있으면 사용, 없으면 saleDate 사용
        return sale.paymentDate ? new Date(sale.paymentDate) : sale.saleDate;
      }
      return sale.saleDate; // 판매일 기준
    };

    if (filters.startDate) {
      filtered = filtered.filter(sale => getFilterDate(sale) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59);
      filtered = filtered.filter(sale => getFilterDate(sale) <= endDate);
    }
    if (filters.saleLocation) {
      filtered = filtered.filter(sale => (sale.saleLocation || sale.channel) === filters.saleLocation);
    }
    if (filters.paymentMethod) {
      filtered = filtered.filter(sale => sale.paymentMethod === filters.paymentMethod);
    }
    if (filters.ownershipType) {
      filtered = filtered.filter(sale => sale.ownershipType === filters.ownershipType);
    }
    if (filters.tradeType) {
      filtered = filtered.filter(sale => sale.tradeType === filters.tradeType);
    }
    if (filters.paymentStatus) {
      filtered = filtered.filter(sale => (sale.paymentStatus || '완납') === filters.paymentStatus);
    }
    if (filters.invoiceIssued !== '') {
      if (filters.invoiceIssued === '발행') {
        // 발행 (전체): 미발행이 아닌 모든 것
        filtered = filtered.filter(sale => {
          const invoiceType = sale.invoiceType || (sale.receiptIssued ? '현금영수증' : (sale.invoiceIssued ? '세금계산서' : '미발행'));
          return invoiceType !== '미발행';
        });
      } else {
        // 특정 타입 필터
        filtered = filtered.filter(sale => {
          const invoiceType = sale.invoiceType || (sale.receiptIssued ? '현금영수증' : (sale.invoiceIssued ? '세금계산서' : '미발행'));
          return invoiceType === filters.invoiceIssued;
        });
      }
    }
    if (filters.shippingStatus) {
      if (filters.shippingStatus === '미배송') {
        // 미배송: 배송완료가 아닌 모든 것
        filtered = filtered.filter(sale => {
          const status = sale.shippingStatus || '준비중';
          return status !== '배송완료' && status !== '완료';
        });
      } else {
        filtered = filtered.filter(sale => (sale.shippingStatus || '준비중') === filters.shippingStatus);
      }
    }

    // 검색 필터
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(sale =>
        (sale.itemName || sale.productName || '').toLowerCase().includes(q) ||
        (sale.buyerName || '').toLowerCase().includes(q) ||
        (sale.designer || '').toLowerCase().includes(q) ||
        (sale.buyerContact || '').includes(q)
      );
    }

    // 날짜 정렬
    filtered.sort((a, b) => {
      const dateA = a.saleDate?.getTime() || 0;
      const dateB = b.saleDate?.getTime() || 0;
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [sales, filters, searchQuery, sortOrder]);

  // 통계 계산
  const stats = useMemo(() => {
    const totalCount = filteredSales.length;
    const totalTransaction = filteredSales.reduce((sum, s) => sum + (s.transactionTotal || s.total || s.actualSalePrice || 0), 0);
    const totalSales = filteredSales.reduce((sum, s) => sum + (s.salesTotal || s.salesBaseAmount || 0), 0);
    const totalProfit = filteredSales.reduce((sum, s) => sum + (s.netProfit || s.profit || 0), 0);
    const totalCardFee = filteredSales.reduce((sum, s) => sum + (s.cardFee || 0), 0);
    const wholesaleSales = filteredSales
      .filter(s => s.tradeType === '도소매')
      .reduce((sum, s) => sum + (s.salesBaseAmount || 0), 0);
    const brokerageSales = filteredSales
      .filter(s => s.tradeType === '중개업')
      .reduce((sum, s) => sum + (s.salesBaseAmount || 0), 0);

    // 미입금 통계
    const unpaidSales = filteredSales.filter(s => (s.paymentStatus || '완납') !== '완납');
    const unpaidCount = unpaidSales.length;
    const unpaidAmount = unpaidSales.reduce((sum, s) => {
      const total = s.transactionTotal || s.total || s.actualSalePrice || 0;
      const paid = s.paidAmount || 0;
      return sum + (total - paid);
    }, 0);

    // 미배송 통계 (배송완료가 아닌 것들)
    const unshippedSales = filteredSales.filter(s => {
      const status = s.shippingStatus || '준비중';
      return status !== '배송완료' && status !== '완료';
    });
    const unshippedCount = unshippedSales.length;

    // 계산서 발행 통계
    const invoiceIssuedCount = filteredSales.filter(s => s.invoiceIssued || s.receiptIssued).length;

    return {
      totalCount,
      totalTransaction,
      totalSales,
      totalProfit,
      totalCardFee,
      wholesaleSales,
      brokerageSales,
      unpaidCount,
      unpaidAmount,
      unshippedCount,
      invoiceIssuedCount
    };
  }, [filteredSales]);

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    const excelData = filteredSales.map((sale, index) => ({
      'date': sale.saleDate?.toLocaleDateString('ko-KR'),
      '입금일': sale.paymentDate || '',
      'name': sale.buyerName || '',
      '연락처': sale.buyerContact || '',
      'Address': sale.buyerAddress || '',
      'Code No.': sale.codeNo || sale.sku || '',
      '대분류': sale.categoryLarge || '',
      '소분류': sale.categoryMedium || '',
      'Item': sale.itemName || sale.productName || '',
      'Designer': sale.designer || '',
      'Manufacturer': sale.manufacturer || '',
      'C/M': sale.ownershipType || '',
      '결제 수단': sale.paymentMethod || '',
      '계산서/영수증': sale.invoiceType || (sale.receiptIssued ? '현금영수증' : (sale.invoiceIssued ? '세금계산서' : '미발행')),
      '발행일': sale.invoiceIssuedDate || '',
      '수금 상태': sale.paymentStatus || '완납',
      '배송 상태': sale.shippingStatus || '준비중',
      '배송 예정일': sale.scheduledDeliveryDate || '',
      '배송 완료일': sale.shippingCompletedDate || '',
      '입금된 금액': sale.paidAmount || 0,
      'Qty': sale.quantity || 1,
      'price': sale.price || sale.unitPrice || 0,
      '배송비': sale.deliveryFee || 0,
      '설치비': sale.installationFee || 0,
      'total': sale.total || sale.actualSalePrice || 0,
      '구매 계약서': sale.hasContract ? 'O' : '',
      'warranty': sale.warranty || '',
      'delivery info': sale.deliveryInfo || '',
      '택배사': sale.courierCompany || '',
      '운송장 번호': sale.trackingNumber || '',
      'Memo': sale.memo || sale.notes || '',
      'seller': sale.seller || '',
      '판매 장소': sale.saleLocation || sale.channel || '',
      '도소매/중개업 구분': sale.tradeType || '',
      '소유자': sale.ownerName || '',
      'PRICE': sale.priceInclVat || sale.total || 0,
      '부가세': sale.vat || 0,
      '매출 기준 금액': sale.salesBaseAmount || 0,
      '원가(매입)': sale.costPrice || sale.purchasePrice || 0,
      '매출액 Total': sale.salesTotal || 0,
      '거래액 Total': sale.transactionTotal || sale.total || 0,
      '순수익': sale.netProfit || sale.profit || 0,
      '수익률(거래액대비)': sale.profitRate ? (sale.profitRate * 100).toFixed(1) + '%' : '',
      '카드수수료율': sale.cardFeeRate ? sale.cardFeeRate + '%' : '',
      '카드수수료': sale.cardFee || 0
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '매출기록');

    const fileName = `매출기록_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  const formatPercent = (rate) => {
    if (!rate && rate !== 0) return '-';
    return (rate * 100).toFixed(1) + '%';
  };

  // 금액 입력용 포맷 (천단위 콤마)
  const formatInputPrice = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('ko-KR').format(value);
  };

  // 콤마 제거하고 숫자로 변환
  const parseInputPrice = (value) => {
    if (!value) return 0;
    return parseInt(String(value).replace(/,/g, '')) || 0;
  };

  // 금액 입력 핸들러
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseInputPrice(value);
    setFormData(prev => ({ ...prev, [name]: numericValue }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  const currentTotals = calculateTotals(formData);

  return (
    <div className="max-w-full mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-brand-900 flex items-center">
          <Icons.TrendingUp className="w-7 h-7 mr-2 text-brand-600" />
          매출 기록
        </h2>
        <div className="flex gap-2">
                      <button
                        onClick={handleExcelDownload}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition flex items-center text-sm font-medium"
                      >
                        <Icons.Excel className="w-4 h-4 mr-1.5" />
                        엑셀 다운로드
                      </button>
                      <button
                        onClick={() => setShowMonthlyStatsModal(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition flex items-center text-sm font-medium"
                      >
                        <Icons.ChartBar className="w-4 h-4 mr-1.5" />
                        월별 통계
                      </button>
                      <button
                        onClick={() => { resetForm(); setShowAddModal(true); }}
                        className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg transition flex items-center text-sm font-medium"
                      >
                        <Icons.Plus className="w-4 h-4 mr-1.5" />
                        매출 등록
                      </button>        </div>
      </div>

      {/* 통계 카드 - 반응형 그리드 */}
      <div className="bg-white rounded-lg shadow-sm border border-brand-100 p-3 md:p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 px-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">판매 건수</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-brand-900 whitespace-nowrap truncate">{stats.totalCount}건</p>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 px-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">총 거래액</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-blue-600 whitespace-nowrap truncate">{formatPrice(stats.totalTransaction)}원</p>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 px-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">총 매출액</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-indigo-600 whitespace-nowrap truncate">{formatPrice(stats.totalSales)}원</p>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 px-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">도소매 매출</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-teal-600 whitespace-nowrap truncate">{formatPrice(stats.wholesaleSales)}원</p>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 px-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">중개업 매출</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-amber-600 whitespace-nowrap truncate">{formatPrice(stats.brokerageSales)}원</p>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 px-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">순수익</p>
            <p className={`text-sm md:text-base lg:text-lg font-bold whitespace-nowrap truncate ${stats.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPrice(stats.totalProfit)}원
            </p>
          </div>
          <div className={`rounded-lg border p-2 px-3 cursor-pointer transition ${filters.paymentStatus === '미입금' ? 'bg-red-200 border-red-400' : 'bg-red-50 border-red-200 hover:bg-red-100'}`} onClick={() => setFilters(prev => ({ ...prev, paymentStatus: (prev.paymentStatus === '미입금' ? '' : '미입금') }))}>
            <p className="text-xs font-medium text-gray-500 mb-0.5">미입금</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-red-600 whitespace-nowrap truncate">{stats.unpaidCount}건</p>
          </div>
          <div className={`rounded-lg border p-2 px-3 cursor-pointer transition ${filters.shippingStatus === '미배송' ? 'bg-orange-200 border-orange-400' : 'bg-orange-50 border-orange-200 hover:bg-orange-100'}`} onClick={() => setFilters(prev => ({ ...prev, shippingStatus: (prev.shippingStatus === '미배송' ? '' : '미배송') }))}>
            <p className="text-xs font-medium text-gray-500 mb-0.5">미배송</p>
            <p className="text-sm md:text-base lg:text-lg font-bold text-orange-600 whitespace-nowrap truncate">{stats.unshippedCount}건</p>
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow-sm border border-brand-100 p-3 md:p-4 mb-4">
        {/* 검색 바 + 필터 토글 */}
        <div className="flex gap-2 md:gap-3">
          <div className="flex-1 relative">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="제품명, 고객명, 디자이너, 연락처 검색..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition ${showFilters ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Icons.Filter className="w-4 h-4" />
            <span className="hidden sm:inline">필터</span>
            {showFilters ? <Icons.ChevronUp className="w-4 h-4" /> : <Icons.ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        {/* 필터 - 접기/펼치기 */}
        <div className={`overflow-hidden transition-all duration-300 ${showFilters ? 'mt-3 max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-11 gap-2 md:gap-3">
          <div className="min-w-0">
            <label className="block text-xs font-bold text-gray-700 mb-1">조회기준</label>
            <select
              value={filters.dateType}
              onChange={(e) => setFilters(prev => ({ ...prev, dateType: e.target.value }))}
              className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-brand-500 ${filters.dateType === 'paymentDate' ? 'border-blue-400 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-300'}`}
            >
              <option value="saleDate">판매일</option>
              <option value="paymentDate">입금일</option>
            </select>
          </div>
          <div className="min-w-0">
            <label className="block text-xs font-bold text-gray-700 mb-1">시작일</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs font-bold text-gray-700 mb-1">종료일</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">판매 장소</label>
            <select
              value={filters.saleLocation}
              onChange={(e) => setFilters(prev => ({ ...prev, saleLocation: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              {config.saleLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">결제수단</label>
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              {config.paymentMethods.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">직판/위탁</label>
            <select
              value={filters.ownershipType}
              onChange={(e) => setFilters(prev => ({ ...prev, ownershipType: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              <option value="직판">직판</option>
              <option value="위탁">위탁</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">도소매/중개</label>
            <select
              value={filters.tradeType}
              onChange={(e) => setFilters(prev => ({ ...prev, tradeType: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              <option value="도소매">도소매</option>
              <option value="중개업">중개업</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">입금 상태</label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              <option value="완납">완납</option>
              <option value="미입금">미입금</option>
              <option value="부분입금">부분입금</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">계산서/영수증</label>
            <select
              value={filters.invoiceIssued}
              onChange={(e) => setFilters(prev => ({ ...prev, invoiceIssued: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              <option value="미발행">미발행</option>
              <option value="세금계산서">세금계산서</option>
              <option value="현금영수증">현금영수증</option>
              <option value="발행">발행 (전체)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">배송 상태</label>
            <select
              value={filters.shippingStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, shippingStatus: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            >
              <option value="">전체</option>
              <option value="준비중">준비중</option>
              <option value="배송중">배송중</option>
              <option value="배송완료">배송완료</option>
              <option value="직접수령">직접수령</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ startDate: '', endDate: '', dateType: 'saleDate', saleLocation: '', paymentMethod: '', ownershipType: '', tradeType: '', paymentStatus: '', invoiceIssued: '', shippingStatus: '' })}
              className="w-full px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition text-sm"
            >
              초기화
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* 매출 목록 - 모바일 카드 뷰 (lg 미만) */}
      <div className="lg:hidden space-y-3">
        {filteredSales.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-brand-100 text-center py-16">
            <Icons.Search className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">매출 내역이 없습니다.</p>
          </div>
        ) : (
          filteredSales.map(sale => {
            const isCompleted = sale.shippingStatus === '배송완료' || sale.shippingStatus === '완료' || sale.shippingCompletedDate;
            return (
              <div
                key={sale.id}
                className="bg-white rounded-lg shadow-sm border border-brand-100 p-4"
              >
                {/* 상단: 날짜, 고객명, 금액 */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-gray-500">{sale.saleDate?.toLocaleDateString('ko-KR')}</p>
                    <p className="font-semibold text-gray-900">{sale.buyerName || '-'}</p>
                    {sale.buyerContact && <p className="text-xs text-gray-400">{sale.buyerContact}</p>}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-brand-600">{formatPrice(sale.transactionTotal || sale.total || sale.actualSalePrice)}원</p>
                    <span className={`inline-block px-1.5 py-0.5 text-xs rounded mt-1 ${sale.saleLocation === '온라인' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                      {sale.saleLocation || sale.channel || '-'}
                    </span>
                  </div>
                </div>

                {/* 제품 정보 */}
                <div className="mb-3 pb-3 border-b border-gray-100">
                  <div className="group relative inline-block">
                    {sale.productId ? (
                      <button
                        onClick={() => setNavigationModal({ sale, productId: sale.productId })}
                        className="text-brand-600 hover:text-brand-800 font-medium hover:underline text-sm"
                      >
                        {(sale.itemName || sale.productName || '').toUpperCase()}
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{(sale.itemName || sale.productName || '').toUpperCase()}</span>
                    )}
                    {/* SKU 호버 툴팁 */}
                    <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                      {sale.soldSkus && sale.soldSkus.length > 0 ? (
                        <>
                          <div className="font-semibold mb-1">판매 SKU ({sale.soldSkus.length}개)</div>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {sale.soldSkus.slice(0, 10).map((sku, i) => (
                              <span key={i} className="bg-gray-700 px-1.5 py-0.5 rounded">{sku}</span>
                            ))}
                            {sale.soldSkus.length > 10 && <span className="text-gray-400">+{sale.soldSkus.length - 10}개</span>}
                          </div>
                        </>
                      ) : (
                        <div className="text-gray-400">SKU 정보 없음</div>
                      )}
                      <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-800 transform rotate-45"></div>
                    </div>
                  </div>
                  {sale.designer && <span className="text-xs text-gray-400 ml-2">{sale.designer}</span>}
                </div>

                {/* 상태 배지들 */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${sale.ownershipType === '위탁' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                    {sale.ownershipType || '직판'}
                  </span>
                  <span className={`px-1.5 py-0.5 text-xs rounded ${sale.tradeType === '중개업' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                    {sale.tradeType || '도소매'}
                  </span>
                  <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                    {sale.paymentMethod || '-'}
                  </span>
                  <span className={`px-1.5 py-0.5 text-xs rounded font-semibold ${
                    (sale.paymentStatus || '완납') === '완납' ? 'bg-green-100 text-green-700' :
                    sale.paymentStatus === '미입금' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {sale.paymentStatus || '완납'}
                  </span>
                  <span className={`px-1.5 py-0.5 text-xs rounded ${isCompleted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {sale.shippingStatus || '준비중'}
                  </span>
                </div>

                {/* 액션 버튼 */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedSaleDetail(sale)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                    >
                      <Icons.Eye className="w-3.5 h-3.5" />
                      상세
                    </button>
                    <button
                      onClick={() => handleEdit(sale)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-brand-50 text-brand-700 rounded hover:bg-brand-100 transition"
                    >
                      <Icons.Edit className="w-3.5 h-3.5" />
                      수정
                    </button>
                    <button
                      onClick={() => handleDelete(sale)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 transition"
                    >
                      <Icons.Trash className="w-3.5 h-3.5" />
                      삭제
                    </button>
                  </div>
                  <button
                    onClick={async () => {
                      if (isCompleted) {
                        await handleStatusChange(sale.id, 'shippingStatus', '배송중');
                        await handleStatusChange(sale.id, 'shippingCompletedDate', '');
                      } else {
                        const today = new Date().toISOString().split('T')[0];
                        await handleStatusChange(sale.id, 'shippingStatus', '배송완료');
                        await handleStatusChange(sale.id, 'shippingCompletedDate', today);
                      }
                    }}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition ${isCompleted ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  >
                    <Icons.Truck className="w-3.5 h-3.5" />
                    {isCompleted ? '배송완료 취소' : '배송완료 처리'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 매출 목록 테이블 - 데스크탑 뷰 (lg 이상) */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-2 py-2 text-center text-xs font-bold text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                  title={sortOrder === 'desc' ? '오래된순으로 정렬' : '최신순으로 정렬'}
                >
                  <div className="flex items-center justify-center gap-1">
                    판매일
                    {sortOrder === 'desc' ? <Icons.ChevronDown className="w-3 h-3" /> : <Icons.ChevronUp className="w-3 h-3" />}
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">입금일</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">고객</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">제품</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">장소</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">결제</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">C/M</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">구분</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">계산서</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">수금상태</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">배송상태</th>
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">배송일정</th>
                <th className="px-2 py-2 text-right text-xs font-bold text-gray-600">거래액(VAT Incl.)</th>
                {/* 매출액, 원가, 수수료, 순수익, 수익률 컬럼 숨김 (기능 유지) */}
                <th className="px-2 py-2 text-center text-xs font-bold text-gray-600">액션</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSales.map(sale => (
                <tr key={sale.id} className="hover:bg-gray-50">
                  <td className="px-2 py-2 whitespace-nowrap text-gray-600 text-xs text-center">
                    {sale.saleDate?.toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <input
                      type="date"
                      value={sale.paymentDate || ''}
                      onChange={(e) => handleStatusChange(sale.id, 'paymentDate', e.target.value)}
                      className={`w-28 text-xs p-1 border rounded-md focus:ring-2 focus:ring-brand-500 ${sale.paymentDate ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="font-medium text-gray-900 text-xs">{sale.buyerName || '-'}</div>
                    <div className="text-xs text-gray-400">{sale.buyerContact}</div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="group relative">
                      {sale.productId ? (
                        <button
                          onClick={() => setNavigationModal({ sale, productId: sale.productId })}
                          className="text-brand-600 hover:text-brand-800 font-medium hover:underline text-xs text-left"
                        >
                          {(sale.itemName || sale.productName || '').toUpperCase()}
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-gray-900">{(sale.itemName || sale.productName || '').toUpperCase()}</span>
                      )}
                      {/* SKU 호버 툴팁 */}
                      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                        {sale.soldSkus && sale.soldSkus.length > 0 ? (
                          <>
                            <div className="font-semibold mb-1">판매 SKU ({sale.soldSkus.length}개)</div>
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {sale.soldSkus.slice(0, 10).map((sku, i) => (
                                <span key={i} className="bg-gray-700 px-1.5 py-0.5 rounded">{sku}</span>
                              ))}
                              {sale.soldSkus.length > 10 && <span className="text-gray-400">+{sale.soldSkus.length - 10}개</span>}
                            </div>
                          </>
                        ) : (
                          <div className="text-gray-400">SKU 정보 없음</div>
                        )}
                        <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-800 transform rotate-45"></div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">{sale.designer}</div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${sale.saleLocation === '온라인' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                      {sale.saleLocation || sale.channel || '-'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-600">
                    {sale.paymentMethod || '-'}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${sale.ownershipType === '위탁' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                      }`}>
                      {sale.ownershipType || '-'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${sale.tradeType === '중개업' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'
                      }`}>
                      {sale.tradeType || '도소매'}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center">
                    {(() => {
                      const currentInvoiceType = sale.invoiceType || (sale.receiptIssued ? '현금영수증' : (sale.invoiceIssued ? '세금계산서' : '미발행'));
                      return (
                        <select
                          value={currentInvoiceType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            if (newType !== '미발행') {
                              // 발행 선택 시 발행일 선택 모달 표시
                              setInvoiceDateModal({
                                show: true,
                                saleId: sale.id,
                                invoiceType: newType,
                                date: sale.invoiceIssuedDate || new Date().toISOString().split('T')[0]
                              });
                            } else {
                              // 미발행 선택 시 바로 저장
                              handleStatusChange(sale.id, 'invoiceType', newType);
                              handleStatusChange(sale.id, 'invoiceIssuedDate', '');
                            }
                          }}
                          className={`w-full text-xs p-1 border rounded-md focus:ring-2 focus:ring-brand-500 bg-transparent font-semibold ${
                            currentInvoiceType === '세금계산서'
                              ? 'border-blue-300 text-blue-700 bg-blue-50'
                              : currentInvoiceType === '현금영수증'
                                ? 'border-green-300 text-green-700 bg-green-50'
                                : 'border-gray-300 text-gray-700'
                          }`}
                          style={{ textAlign: 'center', textAlignLast: 'center' }}
                        >
                          <option value="미발행">미발행</option>
                          <option value="세금계산서">세금계산서</option>
                          <option value="현금영수증">현금영수증</option>
                        </select>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <select
                      value={sale.paymentStatus || '미입금'}
                      onChange={(e) => handleStatusChange(sale.id, 'paymentStatus', e.target.value)}
                      className={`w-full text-xs p-1 border rounded-md focus:ring-2 focus:ring-brand-500 bg-transparent font-semibold ${
                          (sale.paymentStatus || '완납') === '완납'
                          ? 'border-green-300 text-green-700 bg-green-50'
                          : (sale.paymentStatus === '미입금'
                              ? 'border-red-300 text-red-700 bg-red-50'
                              : 'border-yellow-300 text-yellow-700 bg-yellow-50')
                      }`}
                      style={{ textAlign: 'center', textAlignLast: 'center' }}
                    >
                      <option value="완납">완납</option>
                      <option value="부분입금">부분입금</option>
                      <option value="미입금">미입금</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <select
                      value={sale.shippingStatus || '준비중'}
                      onChange={(e) => handleStatusChange(sale.id, 'shippingStatus', e.target.value)}
                      className="w-full text-xs p-1 border rounded-md focus:ring-2 focus:ring-brand-500 bg-transparent border-gray-300 text-gray-700"
                      style={{ textAlign: 'center', textAlignLast: 'center' }}
                    >
                      <option value="준비중">준비중</option>
                      <option value="배송중">배송중</option>
                      <option value="배송완료">배송완료</option>
                      <option value="직접수령">직접수령</option>
                    </select>
                  </td>
                  <td className="px-2 py-2 text-center">
                    {(() => {
                      const isCompleted = sale.shippingStatus === '배송완료' || sale.shippingStatus === '완료' || sale.shippingCompletedDate;
                      return (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="date"
                            value={sale.scheduledDeliveryDate || ''}
                            onChange={(e) => handleStatusChange(sale.id, 'scheduledDeliveryDate', e.target.value)}
                            className={`w-28 text-xs p-1 border rounded-md focus:ring-2 focus:ring-brand-500 ${isCompleted ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
                          />
                          <button
                            onClick={async () => {
                              if (isCompleted) {
                                // 완료 취소
                                await handleStatusChange(sale.id, 'shippingStatus', '배송중');
                                await handleStatusChange(sale.id, 'shippingCompletedDate', '');
                              } else {
                                // 완료 처리
                                const today = new Date().toISOString().split('T')[0];
                                await handleStatusChange(sale.id, 'shippingStatus', '배송완료');
                                await handleStatusChange(sale.id, 'shippingCompletedDate', today);
                              }
                            }}
                            className={`p-1 rounded transition ${isCompleted ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}
                            title={isCompleted ? '배송완료 취소' : '배송완료 처리'}
                          >
                            <Icons.Check className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 text-right text-xs font-medium text-gray-900 whitespace-nowrap">
                    {formatPrice(sale.transactionTotal || sale.total || sale.actualSalePrice)}
                  </td>
                  {/* 매출액, 원가, 수수료, 순수익, 수익률 데이터 숨김 (기능 유지) */}
                  <td className="px-2 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setSelectedSaleDetail(sale)}
                        className="p-1 text-gray-500 hover:text-blue-600"
                        title="상세정보"
                      >
                        <Icons.Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(sale)}
                        className="p-1 text-gray-500 hover:text-brand-600"
                        title="수정"
                      >
                        <Icons.Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(sale)}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title="삭제"
                      >
                        <Icons.Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredSales.length === 0 && (
          <div className="text-center py-16">
            <Icons.Search className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">매출 내역이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 매출 등록/수정 모달 */}
      {
        showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col">
              <div className="bg-brand-900 text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-bold">{editingSale ? '매출 수정' : '매출 등록'}</h3>
                <button onClick={() => { setShowAddModal(false); resetForm(); }} className="text-white hover:text-gray-300">
                  <Icons.Close className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
                {/* 제품 선택 */}
                {!editingSale && (
                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-700 mb-2">제품 선택 (재고에서)</label>
                    {selectedProduct ? (
                      <div className="flex items-center gap-4 p-4 bg-brand-50 rounded-lg border border-brand-200">
                        {selectedProduct.photos?.[0] && (
                          <img src={selectedProduct.photos[0]} alt="" className="w-16 h-16 object-cover rounded" />
                        )}
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{selectedProduct.productName}</p>
                          <p className="text-sm text-gray-600">{selectedProduct.designer} | {selectedProduct.categoryLarge}</p>
                          <p className="text-sm text-brand-600 font-medium">{formatPrice(Math.round(selectedProduct.salePrice * 1.1))}원</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setSelectedProduct(null); }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Icons.Close className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="제품명, SKU, 디자이너로 검색..."
                          value={productSearchQuery}
                          onChange={(e) => { setProductSearchQuery(e.target.value); setShowProductSearch(true); }}
                          onFocus={() => setShowProductSearch(true)}
                          onBlur={() => setTimeout(() => setShowProductSearch(false), 200)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                        />
                        {showProductSearch && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
                            <div className="bg-brand-600 text-white px-4 py-2 flex justify-between items-center sticky top-0">
                              <span className="text-sm font-bold">제품 선택 (재고에서)</span>
                              <button
                                type="button"
                                onClick={() => setShowProductSearch(false)}
                                className="text-white hover:text-gray-200 transition"
                              >
                                <Icons.Close className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="overflow-y-auto max-h-80">
                              {filteredProducts.map(product => (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => handleSelectProduct(product)}
                                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left border-b border-gray-100"
                                >
                                  {product.photos?.[0] ? (
                                    <img src={product.photos[0]} alt="" className="w-10 h-10 object-cover rounded" />
                                  ) : (
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                      <Icons.Box className="w-5 h-5 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{product.productName}</p>
                                    <p className="text-xs text-gray-500">
                                      {product.designer && `${product.designer} | `}
                                      {product.barcodeId || product.sku}
                                    </p>
                                    {/* SKU 정보 표시 */}
                                    {product.skuLocations && product.skuLocations.length > 0 && (
                                      <p className="text-xs text-blue-600 mt-0.5">
                                        SKU {product.skuLocations.length}개: {product.skuLocations.slice(0, 3).map(s => s.sku).join(', ')}
                                        {product.skuLocations.length > 3 && '...'}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-brand-600">{formatPrice(Math.round((product.salePrice || 0) * 1.1))}원</p>
                                    <p className="text-xs text-gray-400">{product.ownershipType}</p>
                                    <p className="text-xs font-medium text-green-600">재고: {product.quantity || 1}개</p>
                                  </div>
                                </button>
                              ))}
                              {filteredProducts.length === 0 && (
                                <p className="p-4 text-center text-gray-500">검색 결과가 없습니다.</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">* 재고에 없는 제품은 아래에서 직접 입력할 수 있습니다.</p>
                  </div>
                )}

                {/* SKU 선택 섹션 - SKU가 있는 제품 선택 시 표시 (신규 등록 시) */}
                {!editingSale && selectedProduct?.skuLocations && selectedProduct.skuLocations.length > 0 && (
                  <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-blue-800 flex items-center">
                        <Icons.Box className="w-4 h-4 mr-2" />
                        SKU 선택 (필수)
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-blue-600">
                          {selectedSkus.length}/{selectedProduct.skuLocations.length}개 선택
                        </span>
                        <button
                          type="button"
                          onClick={handleSelectAllSkus}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          {selectedSkus.length === selectedProduct.skuLocations.length ? '전체 해제' : '전체 선택'}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-40 overflow-y-auto">
                      {selectedProduct.skuLocations.map((skuItem) => (
                        <button
                          key={skuItem.sku}
                          type="button"
                          onClick={() => handleSkuToggle(skuItem.sku)}
                          className={`p-2 rounded-lg border text-center transition ${
                            selectedSkus.includes(skuItem.sku)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          <div className="font-bold text-sm">{selectedProduct.barcodeId}-{skuItem.sku}</div>
                          <div className="text-xs opacity-75 truncate">{skuItem.location || '-'}</div>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      * 판매할 SKU를 선택하세요. 선택한 SKU 수량만큼 자동으로 판매 수량이 설정됩니다.
                    </p>
                  </div>
                )}

                {/* 판매된 SKU 수정 섹션 - 수정 시 표시 (수정 가능) */}
                {editingSale && (
                  <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-orange-800 flex items-center">
                        <Icons.Box className="w-4 h-4 mr-2" />
                        판매 SKU 수정
                      </h4>
                      <span className="text-sm text-orange-600">
                        {editingSkus.length}개 선택됨
                        {editingSale.soldSkus?.length !== editingSkus.length && (
                          <span className="ml-2 text-xs text-orange-500">
                            (기존 {editingSale.soldSkus?.length || 0}개)
                          </span>
                        )}
                      </span>
                    </div>

                    {/* 현재 선택된 SKU (제거 가능) */}
                    <div className="mb-4">
                      <div className="text-xs text-gray-600 mb-2 font-medium">현재 판매 SKU:</div>
                      {editingSkus.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-32 overflow-y-auto">
                          {editingSkus.map((skuItem, idx) => (
                            <div
                              key={typeof skuItem === 'string' ? skuItem : idx}
                              className="p-2 rounded-lg border bg-orange-100 text-orange-800 border-orange-300 text-center relative group"
                            >
                              <button
                                type="button"
                                onClick={() => handleEditingSkuRemove(skuItem)}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 opacity-80 group-hover:opacity-100"
                                title="제거"
                              >
                                X
                              </button>
                              <div className="font-bold text-sm pr-2">
                                {typeof skuItem === 'string' ? skuItem : skuItem.sku}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-3 text-gray-400 bg-gray-100 rounded-lg">
                          선택된 SKU가 없습니다
                        </div>
                      )}
                    </div>

                    {/* 추가 가능한 재고 SKU */}
                    {editingProduct && getAvailableSkusForEdit().length > 0 && (
                      <div>
                        <div className="text-xs text-gray-600 mb-2 font-medium">추가 가능한 재고 SKU:</div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-32 overflow-y-auto">
                          {getAvailableSkusForEdit().map((skuItem) => (
                            <button
                              key={skuItem.sku}
                              type="button"
                              onClick={() => handleEditingSkuAdd(skuItem)}
                              className="p-2 rounded-lg border bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:bg-green-50 text-center transition"
                            >
                              <div className="font-bold text-sm">{editingProduct.barcodeId}-{skuItem.sku}</div>
                              <div className="text-xs text-gray-500 truncate">{skuItem.location || '-'}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 재고에 추가 가능한 SKU가 없는 경우 안내 */}
                    {editingProduct && getAvailableSkusForEdit().length === 0 && editingProduct.skuLocations?.length > 0 && (
                      <div className="text-xs text-gray-500 mt-2 bg-gray-100 p-2 rounded">
                        모든 재고 SKU가 이미 선택되었습니다.
                      </div>
                    )}

                    {/* 제품 정보 로드 실패 시 안내 */}
                    {!editingProduct && editingSale.productId && (
                      <div className="text-xs text-yellow-600 mt-2 bg-yellow-50 p-2 rounded">
                        제품 정보를 불러올 수 없어 SKU를 추가할 수 없습니다. 제거만 가능합니다.
                      </div>
                    )}

                    <p className="text-xs text-orange-600 mt-3">
                      * SKU를 변경하면 재고가 자동으로 조정됩니다. (제거한 SKU는 재고로 복원, 추가한 SKU는 재고에서 차감)
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {/* 1열: 기본 정보 */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-800 border-b pb-2">기본 정보</h4>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">판매일 *</label>
                      <input
                        type="date"
                        name="saleDate"
                        value={formData.saleDate}
                        onChange={handleFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">판매 장소</label>
                      <select
                        name="saleLocation"
                        value={formData.saleLocation}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      >
                        {config.saleLocations.map(loc => (
                          <option key={loc} value={loc}>{loc}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">판매자</label>
                      <input
                        type="text"
                        name="seller"
                        value={formData.seller}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">도소매/중개업</label>
                      <select name="tradeType" value={formData.tradeType} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="도소매">도소매</option>
                        <option value="중개업">중개업</option>
                      </select>
                    </div>
                  </div>

                  {/* 2열: 고객 정보 */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-800 border-b pb-2">고객 정보</h4>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">고객명</label>
                      <input
                        type="text"
                        name="buyerName"
                        value={formData.buyerName}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">연락처</label>
                      <input
                        type="tel"
                        name="buyerContact"
                        value={formData.buyerContact}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">주소 (배송지)</label>
                      <input
                        type="text"
                        name="buyerAddress"
                        value={formData.buyerAddress}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* 3열: 제품 정보 */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-800 border-b pb-2">제품 정보</h4>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Item (제품명) *</label>
                      <input
                        type="text"
                        name="itemName"
                        value={formData.itemName}
                        onChange={handleFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">대분류</label>
                        <input
                          type="text"
                          name="categoryLarge"
                          value={formData.categoryLarge}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">소분류</label>
                        <input
                          type="text"
                          name="categoryMedium"
                          value={formData.categoryMedium}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Designer</label>
                      <input
                        type="text"
                        name="designer"
                        value={formData.designer}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Manufacturer</label>
                      <input
                        type="text"
                        name="manufacturer"
                        value={formData.manufacturer}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">C/M (색상/소재)</label>
                      <input
                        type="text"
                        name="colorMaterial"
                        value={formData.colorMaterial}
                        onChange={handleFormChange}
                        placeholder="예: Black/Leather"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">직판/위탁</label>
                        <select name="ownershipType" value={formData.ownershipType} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="직판">직판</option>
                          <option value="위탁">위탁</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">위탁자/소유자</label>
                        <input
                          type="text"
                          name="ownerName"
                          value={formData.ownerName}
                          onChange={handleFormChange}
                          placeholder="OOM 또는 위탁자명"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 4열: 결제 정보 */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-800 border-b pb-2">결제 정보</h4>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">결제 수단</label>
                      <select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      >
                        {config.paymentMethods.map(method => (
                          <option key={method} value={method}>{method}</option>
                        ))}
                      </select>
                    </div>
                    {formData.paymentMethod === '카드' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">카드사</label>
                        <select name="cardCompany" value={formData.cardCompany} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="">선택</option>
                          {Object.keys(CARD_FEE_RATES).map(card => (
                            <option key={card} value={card}>{card} ({CARD_FEE_RATES[card]}%)</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* {['카드', '토스', '네이버', '페이업'].includes(formData.paymentMethod) && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">카드수수료율 (%)</label>
                        <input
                          type="number"
                          name="cardFeeRate"
                          value={formData.cardFeeRate}
                          onChange={handleFormChange}
                          step="0.1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                        />
                      </div>
                    )} */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">입금 상태</label>
                      <select name="paymentStatus" value={formData.paymentStatus} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="완납">완납</option>
                        <option value="미입금">미입금</option>
                        <option value="부분입금">부분입금</option>
                      </select>
                    </div>
                    {formData.paymentStatus === '부분입금' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">입금된 금액</label>
                        <input
                          type="number"
                          name="paidAmount"
                          value={formData.paidAmount}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">입금일</label>
                      <input
                        type="date"
                        name="paymentDate"
                        value={formData.paymentDate}
                        onChange={handleFormChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 text-sm ${formData.paymentDate ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                      />
                    </div>
                    <div className="flex flex-col gap-2 pt-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">계산서/영수증</label>
                        <select
                          name="invoiceType"
                          value={formData.invoiceType}
                          onChange={handleFormChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 text-sm ${
                            formData.invoiceType === '세금계산서' ? 'border-blue-400 bg-blue-50 text-blue-700' :
                            formData.invoiceType === '현금영수증' ? 'border-green-400 bg-green-50 text-green-700' :
                            'border-gray-300'
                          }`}
                        >
                          <option value="미발행">미발행</option>
                          <option value="세금계산서">세금계산서</option>
                          <option value="현금영수증">현금영수증</option>
                        </select>
                      </div>
                      {formData.invoiceType !== '미발행' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">발행일</label>
                          <input
                            type="date"
                            name="invoiceIssuedDate"
                            value={formData.invoiceIssuedDate}
                            onChange={handleFormChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 금액 정보 */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Qty (수량)
                      {!editingSale && selectedProduct?.skuLocations?.length > 0 && (
                        <span className="text-blue-500 ml-1">(SKU 선택)</span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={!editingSale && selectedProduct?.skuLocations?.length > 0 ? selectedSkus.length : formData.quantity}
                      onChange={handleFormChange}
                      min="1"
                      readOnly={!editingSale && selectedProduct?.skuLocations?.length > 0}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm ${
                        !editingSale && selectedProduct?.skuLocations?.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">price (단가)</label>
                    <input
                      type="text"
                      name="price"
                      value={formatInputPrice(formData.price)}
                      onChange={handlePriceChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm text-right"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">배송비</label>
                    <input
                      type="text"
                      name="deliveryFee"
                      value={formatInputPrice(formData.deliveryFee)}
                      onChange={handlePriceChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm text-right"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">설치비</label>
                    <input
                      type="text"
                      name="installationFee"
                      value={formatInputPrice(formData.installationFee)}
                      onChange={handlePriceChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm text-right"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">원가 (매입가)</label>
                    <input
                      type="text"
                      name="purchasePrice"
                      value={formatInputPrice(formData.purchasePrice)}
                      onChange={handlePriceChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm text-right"
                    />
                  </div>
                </div>

                {/* 배송 & 문서 */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-800 border-b pb-2">배송 정보</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">발송 수단</label>
                        <select name="shippingMethod" value={formData.shippingMethod} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="일정 조율 필요">일정 조율 필요 (물류팀)</option>
                          <option value="자사 물류">자사 물류</option>
                          <option value="외주(퀵)">외주 (퀵)</option>
                          <option value="택배">택배</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">배송 상태</label>
                        <select name="shippingStatus" value={formData.shippingStatus} onChange={handleFormChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="준비중">준비중</option>
                          <option value="배송중">배송중</option>
                          <option value="배송완료">배송완료</option>
                          <option value="직접수령">직접수령</option>
                        </select>
                      </div>
                    </div>

                    {/* 자사 물류 선택 시 - 예정일/시간 */}
                    {formData.shippingMethod === '자사 물류' && (
                      <div className="grid grid-cols-2 gap-2 p-3 bg-blue-50 rounded-lg">
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-1">배송 예정일</label>
                          <input
                            type="date"
                            name="scheduledDeliveryDate"
                            value={formData.scheduledDeliveryDate}
                            onChange={handleFormChange}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 mb-1">배송 예정 시간</label>
                          <input
                            type="time"
                            name="scheduledDeliveryTime"
                            value={formData.scheduledDeliveryTime}
                            onChange={handleFormChange}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {/* 택배 선택 시 - 택배사/운송장 */}
                    {formData.shippingMethod === '택배' && (
                      <div className="grid grid-cols-2 gap-2 p-3 bg-green-50 rounded-lg">
                        <div>
                          <label className="block text-xs font-medium text-green-700 mb-1">택배사</label>
                          <select name="courierCompany" value={formData.courierCompany} onChange={handleFormChange} className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm">
                            <option value="">선택</option>
                            {COURIER_COMPANIES.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-green-700 mb-1">운송장 번호</label>
                          <input
                            type="text"
                            name="trackingNumber"
                            value={formData.trackingNumber}
                            onChange={handleFormChange}
                            className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {/* 일정 조율 필요 선택 시 - 안내 메시지 */}
                    {formData.shippingMethod === '일정 조율 필요' && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-xs text-yellow-700">물류팀에서 고객과 일정을 조율 후 배송 방법과 일정이 확정됩니다.</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">배송 메모</label>
                      <input
                        type="text"
                        name="deliveryInfo"
                        value={formData.deliveryInfo}
                        onChange={handleFormChange}
                        placeholder="예: 부재 시 경비실에 맡겨주세요"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-800 border-b pb-2">문서 & 메모</h4>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="hasContract"
                          checked={formData.hasContract}
                          onChange={handleFormChange}
                          className="w-4 h-4 text-brand-600 rounded"
                        />
                        <span className="text-xs text-gray-700">구매 계약서</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="hasWarranty"
                          checked={formData.hasWarranty}
                          onChange={handleFormChange}
                          className="w-4 h-4 text-brand-600 rounded"
                        />
                        <span className="text-xs text-gray-700">워런티 카드</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Memo</label>
                      <input
                        type="text"
                        name="memo"
                        value={formData.memo}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* 계산 결과 */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-800 mb-3">계산 결과</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">total</p>
                      <p className="font-bold text-gray-900">{formatPrice(currentTotals.total)}원</p>
                    </div>
                    <div>
                      <p className="text-gray-500">PRICE (VAT포함)</p>
                      <p className="font-bold text-gray-900">{formatPrice(currentTotals.priceInclVat)}원</p>
                    </div>
                    <div>
                      <p className="text-gray-500">부가세</p>
                      <p className="font-bold text-gray-900">{formatPrice(currentTotals.vat)}원</p>
                    </div>
                    <div>
                      <p className="text-gray-500">매출 기준 금액</p>
                      <p className="font-bold text-indigo-600">{formatPrice(currentTotals.salesBaseAmount)}원</p>
                    </div>
                    <div>
                      <p className="text-gray-500">원가</p>
                      <p className="font-bold text-gray-600">{formatPrice(currentTotals.costPrice)}원</p>
                    </div>
                    <div>
                      <p className="text-gray-500">거래액 Total</p>
                      <p className="font-bold text-blue-600">{formatPrice(currentTotals.transactionTotal)}원</p>
                    </div>
                    {/* <div>
                      <p className="text-gray-500">카드수수료</p>
                      <p className="font-bold text-orange-600">{formatPrice(currentTotals.cardFee)}원</p>
                    </div> */}
                    <div>
                      <p className="text-gray-500">순수익</p>
                      <p className={`font-bold ${currentTotals.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPrice(currentTotals.netProfit)}원
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">수익률</p>
                      <p className="font-bold text-brand-600">{formatPercent(currentTotals.profitRate)}</p>
                    </div>
                  </div>
                </div>
              </form>

              <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                >
                  취소
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.itemName}
                  className={`px-6 py-2 rounded-lg font-medium text-sm ${formData.itemName
                    ? 'bg-brand-600 hover:bg-brand-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {editingSale ? '수정' : '등록'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* 발행일 선택 모달 */}
      {invoiceDateModal.show && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setInvoiceDateModal({ show: false, saleId: null, invoiceType: '', date: '' })}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div className="px-5 py-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-800">
                  {invoiceDateModal.invoiceType} 발행일 선택
                </h3>
                <button
                  onClick={() => setInvoiceDateModal({ show: false, saleId: null, invoiceType: '', date: '' })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* 날짜 선택 */}
            <div className="p-5">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">발행일</label>
                <input
                  type="date"
                  value={invoiceDateModal.date}
                  onChange={(e) => setInvoiceDateModal(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setInvoiceDateModal({ show: false, saleId: null, invoiceType: '', date: '' })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={async () => {
                    if (invoiceDateModal.saleId && invoiceDateModal.date) {
                      await handleStatusChange(invoiceDateModal.saleId, 'invoiceType', invoiceDateModal.invoiceType);
                      await handleStatusChange(invoiceDateModal.saleId, 'invoiceIssuedDate', invoiceDateModal.date);
                      setInvoiceDateModal({ show: false, saleId: null, invoiceType: '', date: '' });
                    }
                  }}
                  disabled={!invoiceDateModal.date}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                    invoiceDateModal.date
                      ? 'bg-brand-600 hover:bg-brand-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 제품명 클릭 시 이동 선택 모달 */}
      {navigationModal && (() => {
        const matchedProduct = inventory.find(inv => inv.id === navigationModal.productId);
        const productImage = matchedProduct?.photos?.[0];
        return (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setNavigationModal(null)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div className="px-5 py-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-800">
                  이동할 페이지 선택
                </h3>
                <button
                  onClick={() => setNavigationModal(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icons.X className="w-5 h-5" />
                </button>
              </div>
              {/* 제품 이미지 및 이름 */}
              <div className="flex items-center gap-3 mt-3">
                {productImage ? (
                  <img
                    src={productImage}
                    alt={navigationModal.sale.itemName || navigationModal.sale.productName}
                    className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Icons.Inventory className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {(navigationModal.sale.itemName || navigationModal.sale.productName || '').toUpperCase()}
                  </p>
                  <p className="text-xs text-gray-500">{navigationModal.sale.designer || ''}</p>
                </div>
              </div>
            </div>
            {/* 선택 버튼들 */}
            <div className="p-5 space-y-3">
              <button
                onClick={() => {
                  setSelectedSaleDetail(navigationModal.sale);
                  setNavigationModal(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-lg transition-colors"
              >
                <Icons.Eye className="w-5 h-5 text-brand-600" />
                <div className="text-left">
                  <div className="font-medium text-brand-700">매출 상세정보</div>
                  <div className="text-xs text-brand-500">이 판매 건의 상세 내역 보기</div>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate(`/inventory/${navigationModal.productId}`);
                  setNavigationModal(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
              >
                <Icons.Inventory className="w-5 h-5 text-gray-600" />
                <div className="text-left">
                  <div className="font-medium text-gray-700">제품 상세정보</div>
                  <div className="text-xs text-gray-500">제품 페이지로 이동</div>
                </div>
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* 매출 상세정보 모달 */}
      {selectedSaleDetail && (() => {
        const matchedProduct = inventory.find(inv => inv.id === selectedSaleDetail.productId);
        const productImage = matchedProduct?.photos?.[0];
        return (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedSaleDetail(null)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* 모달 헤더 */}
            <div className="px-6 py-4 border-b bg-gradient-to-r from-brand-600 to-brand-500">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Icons.Eye className="w-5 h-5" />
                  매출 상세정보
                </h3>
                <button
                  onClick={() => setSelectedSaleDetail(null)}
                  className="text-white hover:text-gray-200"
                >
                  <Icons.X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* 모달 내용 */}
            <div className="p-6 space-y-6">
              {/* 제품 이미지 (재고와 매칭된 경우) */}
              {productImage && (
                <div className="flex justify-center">
                  <img
                    src={productImage}
                    alt={selectedSaleDetail.itemName || selectedSaleDetail.productName}
                    className="w-48 h-48 object-cover rounded-xl border border-gray-200 shadow-sm"
                  />
                </div>
              )}

              {/* 기본정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.FileText className="w-4 h-4 text-brand-600" />
                  기본정보
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">판매일</span>
                    <p className="font-medium">{selectedSaleDetail.saleDate?.toLocaleDateString('ko-KR')}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">판매처</span>
                    <p className="font-medium">{selectedSaleDetail.saleLocation || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">판매자</span>
                    <p className="font-medium">{getUserDisplayName(selectedSaleDetail.seller) || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">구분</span>
                    <p className="font-medium">
                      <span className={`px-2 py-0.5 rounded text-xs ${selectedSaleDetail.ownershipType === '위탁' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                        {selectedSaleDetail.ownershipType || '직판'}
                      </span>
                      {' / '}
                      <span className={`px-2 py-0.5 rounded text-xs ${selectedSaleDetail.tradeType === '중개업' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                        {selectedSaleDetail.tradeType || '도소매'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* 제품정보 */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.Box className="w-4 h-4 text-blue-600" />
                  제품정보
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="col-span-2">
                    <span className="text-gray-500">제품명</span>
                    <p className="font-medium text-brand-600">{selectedSaleDetail.itemName || selectedSaleDetail.productName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">디자이너</span>
                    <p className="font-medium">{selectedSaleDetail.designer || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">C/M</span>
                    <p className="font-medium">{selectedSaleDetail.colorMaterial || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Code No.</span>
                    <p className="font-medium">{selectedSaleDetail.codeNo || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">카테고리</span>
                    <p className="font-medium">{selectedSaleDetail.categoryLarge} / {selectedSaleDetail.categoryMedium || '-'}</p>
                  </div>
                </div>
              </div>

              {/* 판매 SKU 정보 */}
              <div className="bg-orange-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.Box className="w-4 h-4 text-orange-600" />
                  판매 SKU
                </h4>
                {selectedSaleDetail.soldSkus && selectedSaleDetail.soldSkus.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedSaleDetail.soldSkus.map((sku, idx) => (
                      <span
                        key={typeof sku === 'string' ? sku : idx}
                        className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium"
                      >
                        {typeof sku === 'string' ? sku : sku.sku}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">SKU 정보 없음</p>
                )}
              </div>

              {/* 고객정보 */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.User className="w-4 h-4 text-green-600" />
                  고객정보
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">고객명</span>
                    <p className="font-medium">{selectedSaleDetail.buyerName || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">연락처</span>
                    <p className="font-medium">{selectedSaleDetail.buyerContact || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">주소</span>
                    <p className="font-medium">{selectedSaleDetail.buyerAddress || '-'}</p>
                  </div>
                </div>
              </div>

              {/* 결제정보 */}
              <div className="bg-amber-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.DollarSign className="w-4 h-4 text-amber-600" />
                  결제정보
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">결제방법</span>
                    <p className="font-medium">{selectedSaleDetail.paymentMethod || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">수금상태</span>
                    <p className={`font-medium ${selectedSaleDetail.paymentStatus === '완납' ? 'text-green-600' : selectedSaleDetail.paymentStatus === '미입금' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {selectedSaleDetail.paymentStatus || '완납'}
                    </p>
                  </div>
                  {selectedSaleDetail.cardCompany && (
                    <div>
                      <span className="text-gray-500">카드사</span>
                      <p className="font-medium">{selectedSaleDetail.cardCompany}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">계산서/영수증</span>
                    <p className={`font-medium ${
                      (selectedSaleDetail.invoiceType === '세금계산서' || (!selectedSaleDetail.invoiceType && selectedSaleDetail.invoiceIssued && !selectedSaleDetail.receiptIssued))
                        ? 'text-blue-600'
                        : (selectedSaleDetail.invoiceType === '현금영수증' || (!selectedSaleDetail.invoiceType && selectedSaleDetail.receiptIssued))
                          ? 'text-green-600'
                          : 'text-gray-500'
                    }`}>
                      {selectedSaleDetail.invoiceType || (selectedSaleDetail.receiptIssued ? '현금영수증' : (selectedSaleDetail.invoiceIssued ? '세금계산서' : '미발행'))}
                    </p>
                  </div>
                  {(selectedSaleDetail.invoiceType !== '미발행' && selectedSaleDetail.invoiceType) || selectedSaleDetail.invoiceIssued || selectedSaleDetail.receiptIssued ? (
                    <div>
                      <span className="text-gray-500">발행일</span>
                      <p className="font-medium">{selectedSaleDetail.invoiceIssuedDate || '-'}</p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* 금액정보 */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.Calculator className="w-4 h-4 text-purple-600" />
                  금액정보
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">거래액</span>
                    <p className="font-bold text-gray-900">{formatPrice(selectedSaleDetail.transactionTotal || selectedSaleDetail.total)}원</p>
                  </div>
                  <div>
                    <span className="text-gray-500">매출액(VAT제외)</span>
                    <p className="font-medium text-indigo-600">{formatPrice(selectedSaleDetail.salesBaseAmount || selectedSaleDetail.salesTotal)}원</p>
                  </div>
                  <div>
                    <span className="text-gray-500">원가</span>
                    <p className="font-medium text-gray-600">{formatPrice(selectedSaleDetail.costPrice || selectedSaleDetail.purchasePrice)}원</p>
                  </div>
                  <div>
                    <span className="text-gray-500">배송비</span>
                    <p className="font-medium">{formatPrice(selectedSaleDetail.deliveryFee || 0)}원</p>
                  </div>
                  {/* <div>
                    <span className="text-gray-500">카드수수료</span>
                    <p className="font-medium text-orange-600">{selectedSaleDetail.cardFee > 0 ? formatPrice(selectedSaleDetail.cardFee) + '원' : '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">수수료율</span>
                    <p className="font-medium">{selectedSaleDetail.cardFeeRate > 0 ? selectedSaleDetail.cardFeeRate + '%' : '-'}</p>
                  </div> */}
                </div>
                <div className="mt-4 pt-4 border-t border-purple-200 grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500">순수익</span>
                    <p className={`font-bold text-lg ${(selectedSaleDetail.netProfit || selectedSaleDetail.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPrice(selectedSaleDetail.netProfit || selectedSaleDetail.profit)}원
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">수익률</span>
                    <p className="font-bold text-lg text-brand-600">{formatPercent(selectedSaleDetail.profitRate)}</p>
                  </div>
                </div>
              </div>

              {/* 배송정보 */}
              <div className="bg-indigo-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Icons.Truck className="w-4 h-4 text-indigo-600" />
                  배송정보
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">배송방법</span>
                    <p className="font-medium">{selectedSaleDetail.shippingMethod || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">배송상태</span>
                    <p className={`font-medium ${selectedSaleDetail.shippingStatus === '배송완료' ? 'text-green-600' : selectedSaleDetail.shippingStatus === '배송중' ? 'text-blue-600' : 'text-gray-600'}`}>
                      {selectedSaleDetail.shippingStatus || '준비중'}
                    </p>
                  </div>
                  {selectedSaleDetail.courierCompany && (
                    <div>
                      <span className="text-gray-500">택배사</span>
                      <p className="font-medium">{selectedSaleDetail.courierCompany}</p>
                    </div>
                  )}
                  {selectedSaleDetail.trackingNumber && (
                    <div>
                      <span className="text-gray-500">운송장번호</span>
                      <p className="font-medium">{selectedSaleDetail.trackingNumber}</p>
                    </div>
                  )}
                  {selectedSaleDetail.scheduledDeliveryDate && (
                    <div>
                      <span className="text-gray-500">배송예정일</span>
                      <p className="font-medium">{selectedSaleDetail.scheduledDeliveryDate} {selectedSaleDetail.scheduledDeliveryTime || ''}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 메모 */}
              {selectedSaleDetail.memo && (
                <div className="bg-gray-100 rounded-lg p-4">
                  <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <Icons.FileText className="w-4 h-4 text-gray-600" />
                    메모
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedSaleDetail.memo}</p>
                </div>
              )}

              {/* 변경 이력 */}
              {selectedSaleDetail.changeHistory && selectedSaleDetail.changeHistory.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Icons.Clock className="w-4 h-4 text-slate-600" />
                    변경 이력
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {[...selectedSaleDetail.changeHistory]
                      .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                      .map((history, idx) => {
                        const fieldNames = {
                          paymentStatus: '수금상태',
                          shippingStatus: '배송상태',
                          invoiceIssued: '세금계산서',
                          scheduledDeliveryDate: '배송예정일',
                          shippingCompletedDate: '배송완료일',
                          paymentDate: '입금일',
                          buyerName: '고객명',
                          buyerContact: '연락처',
                          buyerAddress: '주소',
                          memo: '메모',
                          price: '가격',
                          deliveryFee: '배송비'
                        };
                        const fieldName = fieldNames[history.field] || history.field;
                        const formatValue = (val) => {
                          if (val === null || val === undefined || val === '') return '(없음)';
                          if (val === true) return '발행';
                          if (val === false) return '미발행';
                          return String(val);
                        };
                        return (
                          <div key={idx} className="flex items-start gap-3 text-xs border-l-2 border-slate-300 pl-3 py-1">
                            <div className="flex-shrink-0 text-slate-400 w-28">
                              {new Date(history.changedAt).toLocaleString('ko-KR', {
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <div className="flex-1">
                              <span className="font-medium text-slate-700">{fieldName}</span>
                              <span className="text-slate-400 mx-1">:</span>
                              <span className="text-red-500 line-through">{formatValue(history.previousValue)}</span>
                              <span className="text-slate-400 mx-1">→</span>
                              <span className="text-green-600 font-medium">{formatValue(history.newValue)}</span>
                            </div>
                            <div className="flex-shrink-0 text-slate-500">
                              {history.changedByName || getUserDisplayName(history.changedBy) || '알 수 없음'}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
              <div className="text-xs text-gray-500">
                {selectedSaleDetail.productId && (
                  <Link to={`/inventory/${selectedSaleDetail.productId}`} className="text-brand-600 hover:underline">
                    제품 상세보기 →
                  </Link>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    handleEdit(selectedSaleDetail);
                    setSelectedSaleDetail(null);
                  }}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm flex items-center gap-1"
                >
                  <Icons.Edit className="w-4 h-4" />
                  수정
                </button>
                <button
                  onClick={() => setSelectedSaleDetail(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* 월별 통계 모달 */}
      <MonthlyStatsModal
        salesData={sales}
        showModal={showMonthlyStatsModal}
        onClose={() => setShowMonthlyStatsModal(false)}
      />
    </div >
  );
}

export default SalesHistory;
